import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { MarketInfo } from '@/domain/market-info/marketInfo'
import { MarketWalletInfo } from '@/domain/wallet/useMarketWalletInfo'
import { TokenWithBalance } from '@/domain/common/types'
import { z } from 'zod'
import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import { getSwapMaxValue } from './utils'

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function getSwapFormValidator({
  walletInfo,
  marketInfo,
  routerTokens,
}: {
  walletInfo: MarketWalletInfo
  marketInfo: MarketInfo
  routerTokens: TokenWithBalance[]
}) {
  return z.object({
    isMaxSelected: z.boolean(),
    from: getFromFieldValidator({ walletInfo, marketInfo, routerTokens }),
    to: BaseAssetInputSchema,
  })
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function getFromFieldValidator({
  walletInfo,
  marketInfo,
  routerTokens,
}: {
  walletInfo: MarketWalletInfo
  marketInfo: MarketInfo
  routerTokens: TokenWithBalance[]
}) {
  return AssetInputSchema.superRefine((field, ctx) => {
    const value = NormalizedUnitNumber(field.value || 0)
    const balance = walletInfo.findWalletBalanceForSymbol(field.symbol)
    const isNativeAsset = marketInfo.nativeAssetInfo.nativeAssetSymbol === field.symbol
    const bal2 = routerTokens.find((t) => t.token.symbol === field.symbol)?.balance

    const useBal2 = bal2 && bal2.gt(0)
    
    const maxValue = getSwapMaxValue({
      balance: useBal2 ? bal2 : balance,
      isNativeAsset,
      minRemainingNativeAsset: marketInfo.nativeAssetInfo.minRemainingNativeAssetBalance,
    })

    const issue = value.gt(maxValue)

    if (issue) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: isNativeAsset ? 'Not enough HYPE to cover transaction fees' : 'Insufficient balance',
        path: ['value'],
      })
    }
  })
}

const BaseAssetInputSchema = z.object({
  symbol: z.string().transform(TokenSymbol),
  value: z.string().refine(() => true), // @note makes types consistent between input schemas and allows empty strings
})

export const AssetInputSchema = BaseAssetInputSchema.extend({
  value: z.string().refine(
    (data) => {
      if (data === '') {
        return true
      }

      const value = Number.parseFloat(data)
      return !Number.isNaN(value) && value > 0
    },
    {
      message: 'Value must be greater than zero',
    },
  ),
})

export type AssetInputSchema = z.infer<typeof AssetInputSchema>

export type SwapFormSchema = z.infer<ReturnType<typeof getSwapFormValidator>>
